import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Video, Trash2, Star, Upload, Camera, Loader2, AlertCircle, CheckCircle } from 'lucide-react'
import { useDropzone } from 'react-dropzone'
import { thumbnailApi } from '../../services/api/thumbnails'
import { mediaApi } from '../../services/api/media'
import { adminApi } from '../../services/api/admin'
import { useAuth } from '../../hooks/useAuth'

interface MediaFile {
  path: string
  type: 'image' | 'video'
  size?: number
  uploadedAt?: string
  isPrimary?: boolean
}

interface ThumbnailManagerProps {
  modelId: string
  currentThumbnail?: string
  previewVideoPath?: string
  onUpdate: () => void
}

export default function ThumbnailManager({ 
  modelId, 
  currentThumbnail, 
  previewVideoPath,
  onUpdate 
}: ThumbnailManagerProps) {
  const { token } = useAuth()
  const [mediaFiles, setMediaFiles] = useState<MediaFile[]>([])
  const [loading, setLoading] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [selectedFile, setSelectedFile] = useState<MediaFile | null>(null)

  // Load media files
  const loadMediaFiles = async () => {
    setLoading(true)
    setError(null)
    
    try {
      const data = await adminApi.getModelMedia(modelId)
      setMediaFiles(data.data || [])
    } catch (err) {
      console.error('Error loading media:', err)
      setError('Failed to load media files')
      // Fallback: show current files if API fails
      const files: MediaFile[] = []
      if (currentThumbnail) {
        files.push({
          path: currentThumbnail,
          type: 'image',
          isPrimary: true
        })
      }
      if (previewVideoPath) {
        files.push({
          path: previewVideoPath,
          type: 'video'
        })
      }
      setMediaFiles(files)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadMediaFiles()
  }, [modelId])

  // Handle file upload
  const uploadFiles = async (files: File[]) => {
    setUploading(true)
    setError(null)
    setSuccess(null)

    try {
      for (const file of files) {
        const isVideo = file.type.startsWith('video/')
        
        if (isVideo) {
          // Upload video as preview
          await mediaApi.uploadMedia(
            modelId,
            file,
            'preview',
            file.name.endsWith('.gif') ? 'gif' : 'mp4',
            token || undefined
          )
        } else {
          // Upload image as thumbnail
          await thumbnailApi.uploadThumbnail(modelId, file, '1024', token || undefined)
          
          // Also create smaller sizes
          const sizes: Array<'256' | '512'> = ['256', '512']
          for (const size of sizes) {
            await thumbnailApi.uploadThumbnail(modelId, file, size, token || undefined)
          }
        }
      }
      
      setSuccess(`Successfully uploaded ${files.length} file(s)`)
      loadMediaFiles()
      onUpdate()
      
      setTimeout(() => setSuccess(null), 3000)
    } catch (err) {
      console.error('Upload error:', err)
      setError('Failed to upload files')
    } finally {
      setUploading(false)
    }
  }

  // Set primary thumbnail
  const setPrimaryThumbnail = async (path: string) => {
    try {
      await adminApi.setPrimaryThumbnail(modelId, path)
      
      setSuccess('Primary thumbnail updated')
      onUpdate()
      loadMediaFiles()
      
      setTimeout(() => setSuccess(null), 3000)
    } catch (err) {
      console.error('Error setting primary thumbnail:', err)
      setError('Failed to set primary thumbnail')
    }
  }

  // Delete media file
  const deleteMedia = async (path: string) => {
    if (!confirm('Are you sure you want to delete this file?')) return
    
    try {
      await adminApi.deleteMedia(modelId, path)
      
      setSuccess('File deleted successfully')
      loadMediaFiles()
      onUpdate()
      
      setTimeout(() => setSuccess(null), 3000)
    } catch (err) {
      console.error('Error deleting media:', err)
      setError('Failed to delete file')
    }
  }

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: uploadFiles,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.webp'],
      'video/*': ['.mp4', '.webm'],
      'image/gif': ['.gif']
    },
    maxSize: 50 * 1024 * 1024, // 50MB
    disabled: uploading
  })

  const getMediaUrl = (file: MediaFile) => {
    // Use media endpoint for all files with their actual path
    return `${import.meta.env.VITE_API_URL}/api/v2/models/${modelId}/media?path=${encodeURIComponent(file.path)}`
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-gray-900">Media Management</h3>
        <Link
          to={`/admin/models/${modelId}/thumbnail`}
          state={{ from: `/admin/models/${modelId}/edit` }}
          className="inline-flex items-center gap-2 px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
        >
          <Camera size={16} />
          Thumbnail Generator
        </Link>
      </div>

      {/* Media Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-4 mb-4">
          {mediaFiles.map((file, index) => (
            <div
              key={`${file.path}-${index}`}
              className="relative group cursor-pointer"
              onClick={() => setSelectedFile(file)}
            >
              <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden">
                {file.type === 'video' ? (
                  <div className="relative w-full h-full">
                    <video
                      src={getMediaUrl(file)}
                      className="w-full h-full object-cover"
                      muted
                    />
                    <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                      <Video className="w-8 h-8 text-white" />
                    </div>
                  </div>
                ) : (
                  <img
                    src={getMediaUrl(file)}
                    alt="Thumbnail"
                    className="w-full h-full object-cover"
                  />
                )}
              </div>
              
              {/* Overlay buttons */}
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center gap-2">
                {file.type === 'image' && !file.isPrimary && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      setPrimaryThumbnail(file.path)
                    }}
                    className="p-2 bg-white/90 rounded-lg hover:bg-white transition-colors"
                    title="Set as primary thumbnail"
                  >
                    <Star size={16} className="text-gray-700" />
                  </button>
                )}
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    deleteMedia(file.path)
                  }}
                  className="p-2 bg-white/90 rounded-lg hover:bg-white transition-colors"
                  title="Delete"
                >
                  <Trash2 size={16} className="text-red-600" />
                </button>
              </div>
              
              {/* Badges */}
              {file.isPrimary && (
                <div className="absolute top-2 left-2 px-2 py-1 bg-yellow-500 text-white text-xs rounded-full flex items-center gap-1">
                  <Star size={12} fill="currentColor" />
                  Primary
                </div>
              )}
              {file.type === 'video' && (
                <div className="absolute top-2 right-2 px-2 py-1 bg-blue-500 text-white text-xs rounded-full">
                  Preview
                </div>
              )}
            </div>
          ))}
          
          {/* Upload area */}
          <div
            {...getRootProps()}
            className={`aspect-square border-2 border-dashed rounded-lg flex flex-col items-center justify-center cursor-pointer transition-colors ${
              isDragActive ? 'border-indigo-600 bg-indigo-50' : 'border-gray-300 hover:border-gray-400'
            } ${uploading ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <input {...getInputProps()} />
            {uploading ? (
              <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            ) : (
              <>
                <Upload className="w-8 h-8 text-gray-400 mb-2" />
                <p className="text-xs text-gray-500 text-center px-2">
                  {isDragActive ? 'Drop files here' : 'Upload images or videos'}
                </p>
              </>
            )}
          </div>
        </div>
      )}

      {/* Status messages */}
      {success && (
        <div className="flex items-center gap-2 text-green-600 text-sm">
          <CheckCircle size={16} />
          {success}
        </div>
      )}
      {error && (
        <div className="flex items-center gap-2 text-red-600 text-sm">
          <AlertCircle size={16} />
          {error}
        </div>
      )}

      {/* Selected file preview modal */}
      {selectedFile && (
        <div
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedFile(null)}
        >
          <div className="bg-white rounded-lg max-w-3xl max-h-[90vh] overflow-hidden">
            {selectedFile.type === 'video' ? (
              <video
                src={getMediaUrl(selectedFile)}
                controls
                autoPlay
                className="max-w-full max-h-[80vh]"
              />
            ) : (
              <img
                src={getMediaUrl(selectedFile)}
                alt="Preview"
                className="max-w-full max-h-[80vh] object-contain"
              />
            )}
          </div>
        </div>
      )}
    </div>
  )
}